// ── Auto-update: never trapped on a stale cache ──────────────────────────────
// The build bakes in __BUILD_ID__ (unique per build) and ships /version.json with
// the same id. On load, on tab focus / PWA reopen, and periodically, we fetch
// version.json (no-store + cache-busted) and compare. If the DEPLOYED build id
// differs from the one this tab booted with, a newer build is live → purge any
// leftover service worker + caches, then hard-reload so the browser pulls the
// current deploy (index.html is no-store; its hashed bundle is always the latest).
// A per-deploy guard means we force-reload at most once per deployed build, so a
// stuck cache can never cause a reload loop.

const RUNNING = typeof __BUILD_ID__ !== 'undefined' ? __BUILD_ID__ : 'dev'
const POLL_MS = 15000            // check every 15s so a deploy shows up promptly
const FIRST_CHECK_MS = 2000      // and once shortly after first paint
const RELOAD_COOLDOWN_MS = 30000 // don't re-reload for the SAME target within 30s
const GUARD_KEY = 'ctl_reload_guard'

async function purgeServiceWorkersAndCaches() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((r) => r.unregister().catch(() => {})))
    }
  } catch { /* ignore */ }
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => {})))
    }
  } catch { /* ignore */ }
}

let checking = false
async function checkForUpdate() {
  if (checking) return
  checking = true
  try {
    const res = await fetch('/version.json?_=' + Date.now(), { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } })
    if (!res.ok) return
    const data = await res.json().catch(() => null)
    const deployed = data && data.build
    if (!deployed || deployed === RUNNING) return // already on the latest build
    // A newer build is live. Guard against reload LOOPS without giving up forever:
    // if a reload doesn't land the new build (brief post-deploy CDN lag), retry
    // after a short cooldown instead of being stuck until the next deploy. Only
    // the SAME target within the cooldown window is skipped.
    try {
      const raw = sessionStorage.getItem(GUARD_KEY)
      const g = raw ? JSON.parse(raw) : null
      if (g && g.id === deployed && (Date.now() - g.ts) < RELOAD_COOLDOWN_MS) return
      sessionStorage.setItem(GUARD_KEY, JSON.stringify({ id: deployed, ts: Date.now() }))
    } catch { /* ignore */ }
    await purgeServiceWorkersAndCaches()
    window.location.reload()
  } catch { /* ignore */ } finally {
    checking = false
  }
}

export function startAutoUpdate() {
  if (typeof window === 'undefined') return
  // Let the app paint first, then check; re-check when the tab/PWA regains focus
  // (the common "come back after a deploy" case) and on a short interval so a
  // deploy lands on already-open TVs/phones within seconds, no manual refresh.
  setTimeout(checkForUpdate, FIRST_CHECK_MS)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') checkForUpdate() })
  window.addEventListener('focus', checkForUpdate)
  setInterval(checkForUpdate, POLL_MS)
}
